import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Sparkles, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/auth")({
  component: AuthPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as string) || "login",
    };
  },
  head: () => ({
    meta: [
      { title: "Sign in — SmsEmpire" },
      {
        name: "description",
        content:
          "Log in or create your SmsEmpire account to access SMS codes, disposable emails and pre-made accounts.",
      },
      { property: "og:title", content: "Sign in — SmsEmpire" },
      { property: "og:url", content: "/auth" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/auth" }],
  }),
});

function AuthPage() {
  const { tab: initialTab } = Route.useSearch();
  const { t } = useI18n();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"login" | "register">(initialTab as "login" | "register");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate({ to: "/dashboard" });
  }, [user, navigate]);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const emailVal = fd.get("email") as string;
    setLoading(true);
    const res = await login(emailVal, fd.get("password") as string);
    setLoading(false);
    if (!res.ok) {
      if (res.error === "EMAIL_NOT_VERIFIED") {
        toast.info(t("auth.verify_email_first"));
        navigate({ to: "/verify-email", search: { token: "", email: emailVal } });
        return;
      }
      return toast.error(res.error);
    }
    toast.success(t("auth.welcome_back"));
    navigate({ to: "/dashboard" });
  }

  const [refCode, setRefCode] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("vh_ref_code") || localStorage.getItem("ref_code") || "";
    }
    return "";
  });

  async function handleRegister(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setLoading(true);
    const password = fd.get("password") as string;
    const confirmPassword = fd.get("confirmPassword") as string;
    const emailVal = fd.get("email") as string;
    const customRefCode = (fd.get("refCode") as string) || refCode;
    if (password !== confirmPassword) {
      setLoading(false);
      return toast.error(t("auth.passwords_no_match"));
    }
    const res = await register(emailVal, password, fd.get("username") as string, customRefCode);
    setLoading(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(t("auth.register_success"));
    navigate({ to: "/verify-email", search: { token: "", email: emailVal } });
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background p-4">
      <div className="absolute left-4 top-4 z-10 sm:left-8 sm:top-8">
        <Button asChild variant="secondary" size="sm" className="gap-2 shadow-sm">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" /> {t("auth.back")}
          </Link>
        </Button>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-hero" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-primary shadow-glow">
            <Sparkles className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold">SmsEmpire</h1>
          <p className="text-sm text-muted-foreground">{t("app.tagline")}</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
          <Tabs value={tab} onValueChange={(v) => setTab(v as "login" | "register")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t("auth.login")}</TabsTrigger>
              <TabsTrigger value="register">{t("auth.register")}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <p className="mb-4 text-sm text-muted-foreground">{t("auth.subtitle_login")}</p>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="l-identifier">{t("auth.email_or_username")}</Label>
                  <Input
                    id="l-identifier"
                    name="email"
                    type="text"
                    required
                    autoComplete="username"
                    placeholder={t("auth.email_placeholder")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="l-password">{t("auth.password")}</Label>
                  <Input
                    id="l-password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    minLength={6}
                  />
                </div>
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                  >
                    {t("auth.forgot_password")}
                  </Link>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-primary shadow-glow"
                >
                  {t("auth.submit_login")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <p className="mb-4 text-sm text-muted-foreground">{t("auth.subtitle_register")}</p>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="r-username">{t("auth.username")}</Label>
                  <Input id="r-username" name="username" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-email">{t("auth.email")}</Label>
                  <Input id="r-email" name="email" type="email" required autoComplete="email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-password">{t("auth.password")}</Label>
                  <Input
                    id="r-password"
                    name="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-confirm-password">{t("auth.confirm_password")}</Label>
                  <Input
                    id="r-confirm-password"
                    name="confirmPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-ref-code">{t("auth.referral_code_label")}</Label>
                  <Input
                    id="r-ref-code"
                    name="refCode"
                    type="text"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value)}
                    placeholder={t("common.example_value", { value: "a1b2c3d4" })}
                    className="font-mono text-xs uppercase"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-primary shadow-glow"
                >
                  {t("auth.submit_register")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
